import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  markCatalogCategoryAutoPromptShown,
  markCatalogCategoryBannerDismissed,
  markCatalogCategoryWizardCompleted,
  shouldAutoOpenCatalogCategoryWizard,
  shouldShowCatalogCategoryBanner,
} from "../helpers/catalogCategoryOnboardingStorage";
import { userHasAnyRole } from "../services/authService";
import { getStatus } from "../services/onboardingService";

interface UseCatalogCategoryOnboardingOptions {
  tenantId: number | null;
  useCatalogCategories: boolean;
  canConfigure: boolean;
  onWizardComplete: () => void | Promise<void>;
}

async function welcomeIsOnScreen(): Promise<boolean> {
  if (!userHasAnyRole(["owner"])) {
    return false;
  }

  try {
    const response = await getStatus();

    return response.data.welcome?.show === true;
  } catch {
    return false;
  }
}

export function useCatalogCategoryOnboarding({
  tenantId,
  useCatalogCategories,
  canConfigure,
  onWizardComplete,
}: UseCatalogCategoryOnboardingOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categoryWizardOpen, setCategoryWizardOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const refreshBannerVisibility = useCallback(() => {
    setShowBanner(shouldShowCatalogCategoryBanner(tenantId, useCatalogCategories));
  }, [tenantId, useCatalogCategories]);

  useEffect(() => {
    refreshBannerVisibility();
  }, [refreshBannerVisibility]);

  useEffect(() => {
    if (searchParams.get("catalogOnboarding") !== "1" || useCatalogCategories) {
      return;
    }

    let cancelled = false;

    void (async () => {
      if (await welcomeIsOnScreen()) {
        return;
      }

      if (cancelled) {
        return;
      }

      if (canConfigure && tenantId != null) {
        markCatalogCategoryAutoPromptShown(tenantId);
        setCategoryWizardOpen(true);
      }

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("catalogOnboarding");
      setSearchParams(nextParams, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    canConfigure,
    searchParams,
    setSearchParams,
    tenantId,
    useCatalogCategories,
  ]);

  useEffect(() => {
    if (searchParams.get("catalogOnboarding") === "1") {
      return;
    }

    if (
      tenantId == null ||
      !shouldAutoOpenCatalogCategoryWizard(tenantId, useCatalogCategories, canConfigure)
    ) {
      return;
    }

    let cancelled = false;

    void (async () => {
      if (await welcomeIsOnScreen()) {
        return;
      }

      if (cancelled) {
        return;
      }

      markCatalogCategoryAutoPromptShown(tenantId);
      setCategoryWizardOpen(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [canConfigure, searchParams, tenantId, useCatalogCategories]);

  const dismissBanner = useCallback(() => {
    if (tenantId == null) {
      return;
    }

    markCatalogCategoryBannerDismissed(tenantId);
    setShowBanner(false);
  }, [tenantId]);

  const openWizard = useCallback(() => {
    setCategoryWizardOpen(true);
  }, []);

  const closeWizard = useCallback(() => {
    setCategoryWizardOpen(false);
  }, []);

  const completeWizard = useCallback(async () => {
    if (tenantId != null) {
      markCatalogCategoryWizardCompleted(tenantId);
    }

    setShowBanner(false);
    setCategoryWizardOpen(false);
    await onWizardComplete();
  }, [onWizardComplete, tenantId]);

  return {
    showBanner,
    categoryWizardOpen,
    openWizard,
    closeWizard,
    dismissBanner,
    completeWizard,
    refreshBannerVisibility,
  };
}
